import { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY       = 'trackapp:theme';
const FONT_SCALE_KEY    = 'trackapp:fontScale';
const FONT_FAMILY_KEY   = 'trackapp:fontFamily';

// Chart colors are passed as Recharts props and cannot use CSS variables.
// `bg` matches each theme's --surface, for markers that need a solid
// "cutout" stroke against the chart card behind them.
const CHART_DEFAULT = {
  grid:  '#222222',
  axis:  '#3a3a3a',
  tick:  '#707070',
  label: '#525252',
  bg:    '#111111',
};

const CHART_HIGH_CONTRAST = {
  grid:  '#444444',
  axis:  '#888888',
  tick:  '#cccccc',
  label: '#bbbbbb',
  bg:    '#0a0a0a',
};

const CHART_MIDNIGHT = {
  grid:  '#1a2540',
  axis:  '#253560',
  tick:  '#4a6080',
  label: '#3a5070',
  bg:    '#0d1225',
};

const CHART_DAYLIGHT = {
  grid:  '#d8dadb',
  axis:  '#8a9092',
  tick:  '#5c6163',
  label: '#4a4e50',
  bg:    '#ffffff',
};

const CHART_CARBON = {
  grid:  '#2a2a2a',
  axis:  '#383838',
  tick:  '#555555',
  label: '#444444',
  bg:    '#1e1e1e',
};

export const THEMES = {
  default: {
    label: 'Default',
    chart: CHART_DEFAULT,
    '--bg':           '#0d0d0d',
    '--surface':      '#111111',
    '--surface-alt':  '#1a1a1a',
    '--bg-raised':    '#1a1a1a',
    '--border':       '#2c2c2c',
    '--border-strong':'#3a3a3a',
    '--accent':       '#e8411a',
    '--accent-hover': '#ff5a35',
    '--text':         '#e0e0e0',
    '--text-dim':     '#a8a8a8',
    '--muted':        '#787878',
    '--photo-scrim':  'rgba(0, 0, 0, 0.65)',
  },
  'high-contrast': {
    label: 'High Contrast',
    chart: CHART_HIGH_CONTRAST,
    '--bg':           '#000000',
    '--surface':      '#0a0a0a',
    '--surface-alt':  '#1a1a1a',
    '--bg-raised':    '#161616',
    '--border':       '#444444',
    '--border-strong':'#666666',
    '--accent':       '#FFD600',
    '--accent-hover': '#FFE633',
    '--text':         '#ffffff',
    '--text-dim':     '#cccccc',
    '--muted':        '#aaaaaa',
    '--photo-scrim':  'rgba(0, 0, 0, 0.78)',
  },
  midnight: {
    label: 'Midnight',
    chart: CHART_MIDNIGHT,
    '--bg':           '#080c18',
    '--surface':      '#0d1225',
    '--surface-alt':  '#152038',
    '--bg-raised':    '#101a30',
    '--border':       '#1a2540',
    '--border-strong':'#253560',
    '--accent':       '#3D8EFF',
    '--accent-hover': '#6aabff',
    '--text':         '#c8d8f0',
    '--text-dim':     '#7090b0',
    '--muted':        '#4a6080',
    '--photo-scrim':  'rgba(3, 6, 16, 0.7)',
  },
  daylight: {
    label: 'Daylight',
    chart: CHART_DAYLIGHT,
    '--bg':           '#f4f4f4',
    '--surface':      '#ffffff',
    '--surface-alt':  '#eceff0',
    '--bg-raised':    '#f7f8f8',
    '--border':       '#c7cbcd',
    '--border-strong':'#98a0a3',
    '--accent':       '#0B6B5F',
    '--accent-hover': '#095448',
    '--text':         '#141414',
    '--text-dim':     '#4a4a4a',
    '--muted':        '#6b6f70',
    '--photo-scrim':  'rgba(244, 244, 244, 0.82)',
  },
  carbon: {
    label: 'Carbon',
    chart: CHART_CARBON,
    '--bg':           '#141414',
    '--surface':      '#1e1e1e',
    '--surface-alt':  '#2a2a2a',
    '--bg-raised':    '#242424',
    '--border':       '#2a2a2a',
    '--border-strong':'#383838',
    '--accent':       '#00C853',
    '--accent-hover': '#33d66c',
    '--text':         '#d8d8d8',
    '--text-dim':     '#888888',
    '--muted':        '#555555',
    '--photo-scrim':  'rgba(10, 10, 10, 0.68)',
  },
};

// Text size — scales the root font-size, which the rem-based UI follows.
export const FONT_SCALES = {
  sm: { label: 'Small',   scale: 0.875 },
  md: { label: 'Default', scale: 1.0   },
  lg: { label: 'Large',   scale: 1.125 },
  xl: { label: 'X-Large', scale: 1.25  },
};

// Font family — bundled via @fontsource (see main.jsx), so all stacks work offline.
export const FONTS = {
  sans:     { label: 'Sans',     stack: "'Inter', system-ui, -apple-system, sans-serif" },
  serif:    { label: 'Serif',    stack: "Georgia, 'Times New Roman', serif" },
  mono:     { label: 'Mono',     stack: "'JetBrains Mono', ui-monospace, monospace" },
  readable: { label: 'Readable', stack: "'Atkinson Hyperlegible', system-ui, sans-serif" },
};

export function applyTheme(key) {
  const theme = THEMES[key] || THEMES.default;
  const root = document.documentElement;
  Object.entries(theme).forEach(([k, v]) => {
    if (k.startsWith('--')) root.style.setProperty(k, v);
  });
}

export function applyFontScale(key) {
  const fs = FONT_SCALES[key] || FONT_SCALES.md;
  document.documentElement.style.setProperty('--ui-scale', String(fs.scale));
}

export function applyFont(key) {
  const f = FONTS[key] || FONTS.sans;
  document.documentElement.style.setProperty('--app-font', f.stack);
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeKey] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && THEMES[saved] ? saved : 'default';
  });
  const [fontScale, setFontScaleKey] = useState(() => {
    const saved = localStorage.getItem(FONT_SCALE_KEY);
    return saved && FONT_SCALES[saved] ? saved : 'md';
  });
  const [fontFamily, setFontFamilyKey] = useState(() => {
    const saved = localStorage.getItem(FONT_FAMILY_KEY);
    return saved && FONTS[saved] ? saved : 'sans';
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    applyFontScale(fontScale);
    localStorage.setItem(FONT_SCALE_KEY, fontScale);
  }, [fontScale]);

  useEffect(() => {
    applyFont(fontFamily);
    localStorage.setItem(FONT_FAMILY_KEY, fontFamily);
  }, [fontFamily]);

  const chartColors = THEMES[theme]?.chart ?? CHART_DEFAULT;

  return (
    <ThemeContext.Provider value={{
      theme,      setTheme:      setThemeKey,
      fontScale,  setFontScale:  setFontScaleKey,
      fontFamily, setFontFamily: setFontFamilyKey,
      chartColors,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: 'default',  setTheme: () => {},
      fontScale: 'md',   setFontScale: () => {},
      fontFamily: 'sans', setFontFamily: () => {},
      chartColors: CHART_DEFAULT,
    };
  }
  return ctx;
}
